import semver from 'semver'
import { IntentId, StepId } from '@feedyou/schema'
import { createSelector } from 'reselect'
import { stripSecretsFromBotApiUrl } from '@feedyou/utils'

import { RootState, RS } from '../../reducers'

import { BotDeployabilityStatus } from './types'
import { DashboardTab } from '../../constants/tabType'
import { AppState } from '../../reducers/app.types'
import { loadedBotVersionSelector } from '../flow/selectors/loadedBotVersionSelector'
import { BotHeaderTab, BotHeaderTabType } from '../../features/botHeaderTabs/types'
import { isTabClosable, isTabOfType } from '../../features/botHeaderTabs'
import { mainDialogsSelector } from '../flow/selectors/mainDialogsSelector'
import { currentBotConfigurationSelector } from '../../features/serverBots/selectors/currentBotConfigurationSelector'
import { deployedBotVersionSelector } from '../../features/hostingVersions/selectors/deployedVersionSelector'
import { gatedLogger } from '../../lib/gatedLogger'

export const loadedBotApiUrlSelector = createSelector(
  (state: RootState) => currentBotConfigurationSelector(state),
  config => stripSecretsFromBotApiUrl(config?.apiUrl || '')
)

export const getLoadedBotHostingId = createSelector(loadedBotApiUrlSelector, (apiUrl = '') => {
  return apiUrl.match(/\/\/feedbot-([^.]+)/)?.[1]
})

export const appSelector = (state: RS): AppState => state.app.present

export const openBotIdSelector = (state: RS) => appSelector(state).openBotId

export const loadedBotDeployabilityStatusSelector = createSelector(
  loadedBotApiUrlSelector,
  loadedBotVersionSelector,
  (state: RootState) => deployedBotVersionSelector(state),
  (apiUrl, loadedBotVersion, deployedBotVersion): BotDeployabilityStatus => {
    gatedLogger('deployButton', 'loadedBotDeployabilityStatusSelector', {
      loadedBotVersion,
      apiUrl,
      deployedBotVersion
    })

    if (!apiUrl) {
      return BotDeployabilityStatus.NotDeployable
    }

    if (!loadedBotVersion || !deployedBotVersion) {
      return BotDeployabilityStatus.Unknown
    }

    const isCurrentVersionAheadOfDeployedVersion = semver.gt(
      semver.coerce(loadedBotVersion),
      semver.coerce(deployedBotVersion)
    )

    if (isCurrentVersionAheadOfDeployedVersion) {
      gatedLogger('deployButton', 'Current version is ahead of deployed')
      return BotDeployabilityStatus.Deployable
    }

    return BotDeployabilityStatus.NotDeployable
  }
)

export const selectedHeaderTabSectionSelector = (state: RootState) =>
  appSelector(state).selectedHeaderTab

const DEFAULT_TAB = { id: 'main', type: BotHeaderTabType.Dialog } // Static reference is required here

export const selectedHeaderTabSelector = (state: RootState): BotHeaderTab => {
  return selectedHeaderTabSectionSelector(state)?.tab || DEFAULT_TAB
}

export const selectedHeaderTabPendingScrollIdSelector = (
  state: RootState
): StepId | IntentId | undefined => {
  return selectedHeaderTabSectionSelector(state)?.pendingScrollId
}

export function selectedDashboardFilterTabSelector(state: RootState): DashboardTab {
  return appSelector(state)?.selectedDashboardFilterTab || DashboardTab.Active
}

export const openHeaderTabsSelector = (state: RootState): BotHeaderTab[] => {
  return appSelector(state).openHeaderTabs || []
}

export const openStepNlpTabsSelector = createSelector(openHeaderTabsSelector, openTabs => {
  return openTabs.filter(isTabOfType(BotHeaderTabType.StepNlpModel))
})

export const isTabClosableSelector =
  (tab: BotHeaderTab) =>
  (state: RootState): boolean => {
    const mainDialogIds = mainDialogsSelector(state).map(dialog => dialog.id)
    return isTabClosable(tab, mainDialogIds)
  }
